import {recordRumError} from '../../../../util/datadog/datadogRum';
import * as Run from '../../../../util/runs';
import {fromJson} from '../../../../util/runs';
import {detectZeroLengthHistories} from '../../alerts';
import {type BucketedLine} from '../../types';
import {
  type BackendLinesDeltaQuery_Dd_Dq_U_BldqQuery,
  type GroupedBackendLinesDeltaQuery_Dd_Dq_G_BldqQuery,
} from './../../../../generated/graphql';
import {type QueryVars} from './queryVars';
import {
  type BucketedQueryRunsDeltas,
  type GroupedBucketedQueryRunWithGroupedRunConnection,
} from './types';
import {profileRenderTimeSaved} from './utils';

export interface ApplyDeltaResult {
  runsById: Record<string, BucketedLine>;
  order: string[];
}

export function applyBackendLinesDelta({
  currentRunsById,
  delta,
  order,
}: {
  currentRunsById: Record<string, BucketedLine>;
  order: string[];
  delta: NonNullable<
    NonNullable<
      NonNullable<BackendLinesDeltaQuery_Dd_Dq_U_BldqQuery['project']>['runs']
    >['deltas']
  >['delta'];
}): ApplyDeltaResult {
  const deleteOps = Object.keys(currentRunsById).filter(
    rId => !order.includes(rId)
  );

  if (delta.length === 0 && deleteOps.length === 0) {
    return {runsById: currentRunsById, order};
  }

  // Work with a temporary object for all updates
  const tempRunsById = Object.assign({}, currentRunsById);

  deleteOps.forEach(rId => {
    delete tempRunsById[rId];
  });

  delta.forEach(runDelta => {
    if (!runDelta.run) {
      recordRumError(new Error('backend lines error'), {
        reason: 'Bucketed Delta Op returned without valid run',
      });
      return;
    }

    try {
      const parsedRun = Run.fromJson(runDelta.run);
      if (parsedRun == null) {
        recordRumError(new Error('backend lines error'), {
          reason: 'Unable to parse run from server',
          runId: runDelta.run.id,
          displayName: runDelta.run.displayName,
        });
        return;
      }

      tempRunsById[runDelta.run.id] = {
        ...parsedRun,
        history: [], // when we use lines zero out the history
        historyLines: runDelta.run.lines,
      };
    } catch (e) {
      recordRumError(new Error('backend lines error'), {
        reason: 'Error parsing run data when applying delta',
      });
    }
  });

  // Rebuild runsById in the correct order from the API's order array.
  // This ensures that object key iteration order matches the sort order
  // specified by the server.
  const orderedRunsById: Record<string, BucketedLine> = {};
  order.forEach(rId => {
    if (tempRunsById[rId]) {
      orderedRunsById[rId] = tempRunsById[rId];
    }
  });

  profileRenderTimeSaved(orderedRunsById, 'backend');
  return {runsById: orderedRunsById, order};
}

export function applyGroupedBackendLinesDelta({
  currentRunsById,
  delta,
  order,
}: {
  currentRunsById: Record<string, BucketedLine>;
  order: string[];
  delta: NonNullable<
    NonNullable<
      NonNullable<
        GroupedBackendLinesDeltaQuery_Dd_Dq_G_BldqQuery['project']
      >['groupedRuns']
    >['deltas']
  >['delta'];
}): ApplyDeltaResult {
  const deleteOps = Object.keys(currentRunsById).filter(
    rId => !order.includes(rId)
  );
  if (delta.length === 0 && deleteOps.length === 0) {
    return {runsById: currentRunsById, order};
  }

  // Work with a temporary object for all updates
  const tempRunsById = Object.assign({}, currentRunsById);
  deleteOps.forEach(rId => {
    delete tempRunsById[rId];
  });

  delta.forEach(runDelta => {
    if (runDelta.__typename !== 'GroupBucketDiff') {
      // This should never happen because we check the typename before calling this function,
      // but it's required for type safety
      recordRumError(new Error('backend lines error'), {
        reason: 'Grouped Bucketed Delta Op called on non-grouped run',
        runId: runDelta.run?.id,
      });
      return;
    }
    if (!runDelta.groupedRun) {
      recordRumError(new Error('backend lines error'), {
        reason: 'Grouped Bucketed Delta Op returned without valid run',
      });
      return;
    }
    // RunFromGraphQL doesn't allow null for createdAt
    const groupedRun = {
      ...runDelta.groupedRun,
      createdAt: runDelta.groupedRun.createdAt ?? undefined,
    };

    try {
      const parsedRun = Run.fromJson(groupedRun);
      if (parsedRun == null) {
        recordRumError(new Error('backend lines error'), {
          reason: 'Unable to parse grouped run from server',
          groupedRunId: groupedRun.id, // Note: This is only the ID of one of the runs in the group
          displayName: groupedRun.displayName,
        });
        return;
      }

      // Update the entire record for any diffOp
      tempRunsById[groupedRun.id] = {
        ...parsedRun,
        history: [], // when we use lines zero out the history
        historyLines: groupedRun.lines,
        groupTotalRuns: runDelta.groupedRun.totalRuns,
        groupIncludedRuns: runDelta.groupedRun.includedRuns,
      };
    } catch (e) {
      recordRumError(new Error('backend lines error'), {
        reason: 'Error applying grouped backend lines delta',
        error: e instanceof Error ? e.message : 'Unknown error',
        groupedRunId: groupedRun.id,
        displayName: groupedRun.displayName,
      });
    }
  });

  // Rebuild runsById in the correct order from the API's order array.
  // This ensures that object key iteration order matches the sort order
  // specified by the server
  const orderedRunsById: Record<string, BucketedLine> = {};
  order.forEach(rId => {
    if (tempRunsById[rId]) {
      orderedRunsById[rId] = tempRunsById[rId];
    }
  });

  profileRenderTimeSaved(orderedRunsById, 'grouped');
  return {runsById: orderedRunsById, order};
}

export function applyDelta({
  currentRunsById,
  delta,
  order,
  specs,
}: {
  currentRunsById: Record<string, BucketedLine>;
  order: string[];
  delta: BucketedQueryRunsDeltas;
  specs: QueryVars['bucketedHistorySpecs'];
}): ApplyDeltaResult {
  const newRunsById = delta.reduce(
    (acc, runDelta) => {
      const parsedRun = fromJson(runDelta.run);
      if (parsedRun == null || runDelta.run == null) {
        recordRumError(new Error('Runs_line_plot_error'), {
          reason: 'Unable to apply delta',
          runId: runDelta.run?.id,
          displayName: runDelta.run?.displayName,
        });
        return acc;
      }

      detectZeroLengthHistories({
        histories: runDelta.run?.bucketedHistory ?? [],
        id: runDelta.run.id,
        displayName: runDelta.run.displayName ?? '',
        specs,
      });

      acc[runDelta.run.id] = {
        ...parsedRun,
        history: runDelta.run?.bucketedHistory ?? [],
      };
      return acc;
    },
    {} as Record<string, BucketedLine>
  );

  /**
   * IMPORTANT: Object key insertion order matters here!
   * We intentionally rebuild runsById with keys in the order specified by the API's
   * `order` array. This ensures that downstream code using Object.values() or
   * Object.keys() will iterate in the correct sort order.
   *
   * TODO: This relies on JavaScript's implicit object key iteration order, which is
   * fragile. The proper fix is to return {runsById, order} and have consumers
   * explicitly use the order array rather than relying on object key order.
   */
  const runsById: Record<string, BucketedLine> = {};
  order.forEach(rId => {
    runsById[rId] = newRunsById[rId] ?? currentRunsById[rId];
  });

  return {runsById, order};
}

// Narrowed deltas type specifically for the GroupedRunConnection branch so array elements
// are not inferred as any when reducing over union-shaped arrays
export type GroupedBucketedRunDeltas = NonNullable<
  NonNullable<GroupedBucketedQueryRunWithGroupedRunConnection['deltas']>
>['delta'];

export function applyGroupedDelta({
  currentRunsById,
  delta,
  order,
  specs,
}: {
  currentRunsById: Record<string, BucketedLine>;
  order: string[];
  delta: GroupedBucketedRunDeltas;
  specs: QueryVars['bucketedHistorySpecs'];
}): ApplyDeltaResult {
  const newRunsById = delta.reduce(
    (acc, runDelta) => {
      if (runDelta.__typename !== 'GroupBucketDiff') {
        recordRumError(new Error('Runs_line_plot_error'), {
          reason: 'Grouped Bucketed Delta Op called on non-grouped run',
          typename: runDelta.__typename,
        });
        return acc;
      }
      if (!runDelta.groupedRun) {
        recordRumError(new Error('Runs_line_plot_error'), {
          reason: 'Grouped Bucketed Delta Op returned without groupedRun',
        });
        return acc;
      }
      // RunFromGraphQL doesn't allow null for createdAt
      const groupedRun = {
        ...runDelta.groupedRun,
        createdAt: runDelta.groupedRun.createdAt ?? undefined,
      };

      const parsedRun = fromJson(groupedRun);
      if (parsedRun == null) {
        recordRumError(new Error('Runs_line_plot_error'), {
          reason: 'Unable to apply grouped delta',
          groupedRunId: groupedRun.id, // Note: This is only the ID of one of the runs in the group
          displayName: groupedRun.displayName,
        });
        return acc;
      }

      detectZeroLengthHistories({
        histories: runDelta.groupedRun?.bucketedHistory ?? [],
        id: runDelta.groupedRun.id,
        displayName: runDelta.groupedRun.displayName ?? '',
        specs,
      });
      acc[runDelta.groupedRun.id] = {
        ...parsedRun,
        history: runDelta.groupedRun?.bucketedHistory ?? [],
        groupTotalRuns: runDelta.groupedRun.totalRuns,
        groupIncludedRuns: runDelta.groupedRun.includedRuns,
      };
      return acc;
    },
    {} as Record<string, BucketedLine>
  );

  /**
   * IMPORTANT: Object key insertion order matters here!
   * We intentionally rebuild runsById with keys in the order specified by the API's
   * `order` array. This ensures that downstream code using Object.values() or
   * Object.keys() will iterate in the correct sort order.
   *
   * TODO: This relies on JavaScript's implicit object key iteration order, which is
   * fragile. The proper fix is to return {runsById, order} and have consumers
   * explicitly use the order array rather than relying on object key order.
   */
  const runsById: Record<string, BucketedLine> = {};
  order.forEach(rId => {
    runsById[rId] = newRunsById[rId] ?? currentRunsById[rId];
  });

  return {runsById, order};
}
